import { ref, computed } from 'vue'
import { useCasinoStore, type CasinoState } from '~/stores/casino.store'
import { StringUtils } from '~/utils'
import { useGames } from '~composables/games/use-games'
import { useApi } from '~composables/common/use-api'
import { casinoProviders, casinoCategories } from '~/resources/casino/data'
import { GAMES_API } from '~/configs/api'
import { LIVE_CASINO_PATH } from '~/constants'
import type { DropdownItem } from '~/composables/common/interface'
import type { CasinoItemGame } from '~composables/casino/interface'
import { ResponseStatus, CasinoPerPage } from '~composables/common/interface'

const limitGamesIndirect = 6
const comingSoonGameItem = {
  img: { thumbnail: { url: '/assets/images/components/lobby-games/game-coming-soon-empty.png' } },
  title: 'ComingSoon',
  class: 'coming-soon'
}

export function useCasino() {
  const router = useRouter()
  const route = useRoute()

  const { request } = useApi()
  const { playGame } = useGames()
  const storeCasino = useCasinoStore()

  const loadingClass = ref(true)
  const category = ref(typeof route.query.category === 'string' ? route.query.category : '')
  const provider = ref(typeof route.query.provider === 'string' ? route.query.provider : '')
  const limit = ref(CasinoPerPage.DEFAULT)
  const categories = ref<DropdownItem[]>([])
  const providers = ref<DropdownItem[]>([])
  const mainGames = ref<CasinoItemGame[]>([])
  const indirectGames = ref<CasinoItemGame[]>([])
  const keywordSearch = ref('')

  const normalizedSearch = computed(() => StringUtils.normalizeDiacriticsString(keywordSearch.value.toLowerCase()))

  const filteredGames = computed(() =>
    mainGames.value.filter(item => {
      if (provider.value) {
        if (!item.provider || item.provider !== provider.value) {
          return false
        }
      }
      if (category.value) {
        if (
          !item.category ||
          (item.category === 'string' && item.category !== category.value) ||
          !item.category.includes(category.value)
        ) {
          return false
        }
      }
      return StringUtils.normalizeDiacriticsString(item.title?.toLowerCase()).includes(normalizedSearch.value)
    })
  )

  const totalGames = computed(() => filteredGames.value.length)

  const gamesToShow = computed(() => filteredGames.value.slice(0, limit.value))

  async function playCasinoGame(item: CasinoItemGame): Promise<void> {
    void playGame({
      provider: item.provider ?? '',
      apiUrl: GAMES_API.PLAY,
      gameId: item.gameId ?? item.game_id ?? item.tableId ?? '',
      gameType: item.gameType,
      newTab: true,
      promotionPrevent: true,
      promotionPrevent100K: true,
      loginRequired: true,
      withoutIframe: true,
      isCasino: true
    })
  }

  const setCasinoRefs = (data: CasinoState) => {
    providers.value = casinoProviders.filter(item => !item.value || data.providers.has(item.value))
    categories.value = casinoCategories.filter(item => !item.value || data.categories.has(item.value))
    mainGames.value = data.mainGames
    indirectGames.value =
      data.indirectGames.length < limitGamesIndirect
        ? [...data.indirectGames, ...Array(limitGamesIndirect - data.indirectGames.length).fill(comingSoonGameItem)]
        : data.indirectGames
    loadingClass.value = false
  }

  const setupLiveCasinoData = async () => {
    loadingClass.value = true
    const casinoData = storeCasino.getCasinoStore()
    if (casinoData.mainGames.length || casinoData.indirectGames.length) {
      setCasinoRefs(casinoData)
    } else {
      try {
        const { data, status } = await request(GAMES_API.LIVE_CASINO, { method: 'GET' })
        if (status === ResponseStatus.OK) {
          const dataArray: CasinoItemGame[] = Array.isArray(data) ? data : [data]
          const reducedData = dataArray.reduce<CasinoState>(
            (result, game) => {
              const isIndirectGame = game.provider === 'bong88'
              if (!isIndirectGame) {
                if (typeof game.category === 'string') {
                  result.categories.add(game.category)
                } else {
                  game.category?.forEach(categoryItem => result.categories.add(categoryItem))
                }
              }
              return {
                providers: isIndirectGame || !game.provider ? result.providers : result.providers.add(game.provider),
                categories: result.categories,
                mainGames: isIndirectGame ? result.mainGames : [...result.mainGames, game],
                indirectGames: isIndirectGame ? [...result.indirectGames, game] : result.indirectGames
              }
            },
            { providers: new Set(), categories: new Set(), mainGames: [], indirectGames: [] }
          )
          storeCasino.setCasinoStore(reducedData)
          setCasinoRefs(reducedData)
        }
      } catch {
        loadingClass.value = false
      }
    }
  }

  function playGameIndirect(url: string | undefined): void {
    window.open(url, '_blank')
  }

  const setLiveCasinoParamsUrl = (): void => {
    const params: { provider?: string; category?: string } = {}
    if (provider.value) {
      params.provider = provider.value
    }
    if (category.value) {
      params.category = category.value
    }
    void router.push({ path: LIVE_CASINO_PATH, query: params })
  }

  const selectProvider = (selected: string) => {
    provider.value = selected
    limit.value = CasinoPerPage.DEFAULT
    setLiveCasinoParamsUrl()
  }

  const selectCategory = (selected: string) => {
    category.value = selected
    limit.value = CasinoPerPage.DEFAULT
    setLiveCasinoParamsUrl()
  }

  const loadMore = () => {
    limit.value += CasinoPerPage.DEFAULT
  }

  watch(
    () => keywordSearch.value,
    () => (limit.value = CasinoPerPage.DEFAULT)
  )

  useAsyncData('casino', async () => {
    await setupLiveCasinoData()
  })

  return {
    limit,
    category,
    provider,
    loadMore,
    providers,
    categories,
    totalGames,
    gamesToShow,
    loadingClass,
    keywordSearch,
    indirectGames,
    selectProvider,
    selectCategory,
    playCasinoGame,
    playGameIndirect
  }
}
